// Connection tier (TAS-398).
//
// Prod 4458 (19 Sep): a table on slow wifi waited on assets the room could not
// afford. The reveal costs about 5.6 MB for the first wine with everything on —
// a 2.7 MB film, a 0.9 MB bottle model, a 1.5 MB lighting map and the 3D
// runtime — and about 3.6 MB for each wine after. On a connection doing 50 KB/s
// that is a minute of nothing.
//
// perfTier answers "can this DEVICE afford the full show?" and memoizes forever,
// which is right for hardware. This module answers "can this CONNECTION afford
// it right now?", which is a different question with a different shape: it
// changes during the evening, it cannot be read reliably off the browser, and it
// has to be allowed to change its mind.
//
// ── Why it is measured, not asked ────────────────────────────────────────────
// navigator.connection does not exist on iOS Safari, which is most of a table.
// So the tier is taken from how long this game's OWN downloads actually took,
// and navigator.connection is only a hint used before any sample exists.
//
// Three sources, because no single one can see everything:
//
//   1. Resource timing, for SAME-ORIGIN assets. It cannot see the ones that
//      matter most: Supabase storage serves the models and the films with
//      `Access-Control-Allow-Origin: *` but NO `Timing-Allow-Origin`, so the
//      browser reports transferSize 0 for every one of them (Codex; confirmed
//      against the live headers). Permission to READ a resource is not
//      permission to TIME it.
//   2. Explicit samples from fetches this app performs itself — the bottle
//      model and its lighting map. Our own clock and our own byte count, so
//      the CORS masking does not apply.
//   3. A one-off probe of a same-origin asset when neither of the above has
//      produced anything, so a warm cache and a quiet start cannot leave the
//      tier guessing all evening.
//
// ── Tiers ────────────────────────────────────────────────────────────────────
//   full    the film and the 3D bottle (reveal + tasting notes)
//   middle  the film, and the bottle photo in place of the model
//   slow    the bottle photo immediately: no film, no model
//
// Override for testing, matching perfTier's convention:
//   localStorage.tasted_net = 'full' | 'middle' | 'slow'   (or ?net= in the URL)

export const NET_FULL = 'full';
export const NET_MIDDLE = 'middle';
export const NET_SLOW = 'slow';

const ORDER = { [NET_SLOW]: 0, [NET_MIDDLE]: 1, [NET_FULL]: 2 };
const isTier = (t) => Object.hasOwn(ORDER, t);

/** The lower (more cautious) of two tiers. */
export function worseTier(a, b) {
  if (!isTier(a)) return b;
  if (!isTier(b)) return a;
  return ORDER[a] <= ORDER[b] ? a : b;
}

// Throughput boundaries, in kilobits per second of OBSERVED transfer, set
// against what the reveal actually weighs.
//
// FULL asks for the first wine's whole bundle — 2.7 MB of film, a 0.9 MB model,
// the 1.5 MB lighting map and the 3D runtime, about 5.6 MB — inside the time
// the room spends between the curtain and the bottle. At 2500 kbps that is
// roughly eighteen seconds, with the model arriving while the film plays.
//
// MIDDLE keeps the film, and a film is watched while it downloads: the pipe has
// to outpace playback or it stalls halfway through the reveal. A 2.7 MB clip at
// 1000 kbps takes about twenty-two seconds, which a fifteen-second film just
// about survives with the get-ready beat in front of it. Below that it does
// not, which is why below that there is no film at all (Codex was right that
// the old 400 floor let through a fifty-four-second download).
const FULL_FLOOR_KBPS = 2500;
const MIDDLE_FLOOR_KBPS = 1000;

// Samples smaller than this say more about latency than bandwidth, and the
// mistake is asymmetric: 20 KB behind 200 ms of setup reads as 769 kbps on a
// 20 Mbit link, and we would take the 3D away from a table that could have had
// it (Codex). 50 KB over the same delay is a number worth believing.
// Only transfers big enough to be about bandwidth count. The compressed reveal
// films are 150–300 KB (2026-09-25), and at that size the time is mostly the
// wait for the first byte, so a fast laptop read as 'middle' and never got the
// 3D bottle (Mel, 2026-09-27). Models (1.5–7 MB) and the HDR still count.
const MIN_SAMPLE_BYTES = 400_000;
const PROBE_MIN_BYTES = 50_000;

// Weight on the newest sample. High on purpose: the point is to notice the
// evening getting worse, not to average across it.
const EWMA_ALPHA = 0.5;

// A single bad sample drops the tier at once — a phone that is struggling
// should not be handed another 3 MB to prove it. Going back up takes agreement
// from two in a row, so one lucky cached response cannot restore the full show.
const UPGRADES_NEEDED = 2;

let ewmaKbps = null;
let sampleCount = 0;
let current = null;         // last tier reported to subscribers
let pendingUpgrade = null;  // tier seen on the way up, waiting for a second
let pendingUpgradeCount = 0;
let sessionCap = null;      // set from the session's features (host override)
let observer = null;
let observerStarted = false;
let probed = false;
let bigSamples = 0;         // samples from real assets, as opposed to the probe
let lastProbeAt = 0;
let probing = false;

const listeners = new Set();

function nav() {
  return typeof navigator !== 'undefined' ? navigator : null;
}

function readOverride() {
  try {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem('tasted_net');
      if (isTier(stored)) return stored;
    }
  } catch { /* storage blocked */ }
  try {
    if (typeof window !== 'undefined' && window.location?.search) {
      const fromUrl = new URLSearchParams(window.location.search).get('net');
      if (isTier(fromUrl)) return fromUrl;
    }
  } catch { /* malformed URL */ }
  return null;
}

/** The browser's own guess, used only until a real sample arrives. */
function hintedTier() {
  const conn = nav()?.connection;
  if (!conn) return null; // iOS Safari — measurement is the only source
  if (conn.saveData) return NET_SLOW; // the player asked for less data
  const effective = conn.effectiveType;
  if (effective === 'slow-2g' || effective === '2g') return NET_SLOW;
  if (effective === '3g') return NET_MIDDLE;
  if (effective === '4g') {
    // effectiveType saturates at '4g', so a genuinely fast link and a
    // congested one look identical here. downlink separates them when present.
    if (typeof conn.downlink === 'number' && conn.downlink > 0 && conn.downlink < 1.5) return NET_MIDDLE;
    return NET_FULL;
  }
  return null;
}

function tierForKbps(kbps) {
  if (kbps >= FULL_FLOOR_KBPS) return NET_FULL;
  if (kbps >= MIDDLE_FLOOR_KBPS) return NET_MIDDLE;
  return NET_SLOW;
}

/** What the evidence says, before the session cap and the override. */
function measuredTier() {
  if (ewmaKbps !== null) return tierForKbps(ewmaKbps);
  return hintedTier() ?? NET_FULL; // nothing known yet: start generous, degrade fast
}

function emit(tier) {
  for (const fn of listeners) {
    try { fn(tier); } catch { /* a listener must not break the others */ }
  }
}

/**
 * The tier to render at right now.
 * Order of authority: manual override, then the session cap the host set, then
 * what the connection has actually done.
 */
export function netTier() {
  const override = readOverride();
  if (override) return override;
  startObserver();
  const measured = current ?? measuredTier();
  return sessionCap ? worseTier(measured, sessionCap) : measured;
}

/** True when this connection can afford the 3D bottle and its lighting map. */
export function canAfford3D() {
  return netTier() === NET_FULL;
}

/** True when this connection can afford the reveal film. */
export function canAffordVideo() {
  return netTier() !== NET_SLOW;
}

/**
 * Fold one observed transfer into the estimate.
 *
 * `provisional` marks the probe: a 200 KB reading taken because nothing else
 * had been measured. It is enough to decide the first wine, but it is not
 * evidence to hold the evening to. Until a real asset has been measured, each
 * newer reading (a re-probe, or the first real asset) replaces the one before
 * it outright, in either direction, instead of being averaged with it or made
 * to agree twice (prod 2476, 2026-10-02: phones on decent wifi never got the
 * 3D bottle). Once real assets have spoken, the ordinary rules apply.
 * @param {{bytes: number, ms: number, minBytes?: number, provisional?: boolean}} sample
 */
export function recordAssetSample({ bytes, ms, minBytes = MIN_SAMPLE_BYTES, provisional = false } = {}) {
  if (!Number.isFinite(bytes) || !Number.isFinite(ms)) return;
  if (bytes < minBytes || ms <= 0) return;

  // Read where we stood BEFORE folding this sample in — measuredTier() is
  // computed from the estimate, so taking it afterwards compares the new
  // reading against itself and nothing ever changes.
  //
  // Committing it to `current` here matters as much: while `current` stayed
  // null, netTier() kept deriving a fresh answer from the moving estimate, so
  // the getter could already say 'full' during a staged upgrade that had not
  // notified anyone — subscribers on 'slow', direct callers on 'full' (Codex).
  const previous = current ?? measuredTier();
  current = previous;

  const kbps = (bytes * 8) / ms; // bytes/ms * 8 === kilobits/second
  const onlyProbeSoFar = bigSamples === 0 && sampleCount > 0;
  ewmaKbps = ewmaKbps === null || onlyProbeSoFar ? kbps : (EWMA_ALPHA * kbps) + ((1 - EWMA_ALPHA) * ewmaKbps);
  sampleCount += 1;
  if (!provisional) bigSamples += 1;

  const next = tierForKbps(ewmaKbps);
  if (onlyProbeSoFar && next !== previous) {
    // A newer reading overrules the probe's guess at once.
    pendingUpgrade = null;
    pendingUpgradeCount = 0;
    current = next;
    emit(netTier());
    return;
  }
  if (next === previous) {
    current = next;
    pendingUpgrade = null;
    pendingUpgradeCount = 0;
    return;
  }

  if (ORDER[next] < ORDER[previous]) {
    // Down: immediately.
    pendingUpgrade = null;
    pendingUpgradeCount = 0;
    current = next;
    emit(netTier());
    return;
  }

  // Up: only on agreement.
  if (pendingUpgrade === next) pendingUpgradeCount += 1;
  else { pendingUpgrade = next; pendingUpgradeCount = 1; }
  if (pendingUpgradeCount >= UPGRADES_NEEDED) {
    pendingUpgrade = null;
    pendingUpgradeCount = 0;
    current = next;
    emit(netTier());
  }
}

/**
 * Watch every asset the game downloads, so the tier is informed by real traffic
 * rather than a probe we invented. Safe to call repeatedly.
 */
export function startObserver() {
  if (observerStarted) return;
  observerStarted = true;
  if (typeof PerformanceObserver === 'undefined') return;

  try {
    observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // transferSize is 0 for a cached response and for a cross-origin
        // response without Timing-Allow-Origin — neither says anything about
        // the pipe, so neither becomes a sample.
        const bytes = entry.transferSize;
        if (!bytes) continue;
        // responseStart → responseEnd is the body coming down the wire.
        // `duration` also carries queueing, DNS and TLS, which belong to the
        // first byte and must not be extrapolated across a 3 MB film.
        const transferMs = entry.responseEnd - entry.responseStart;
        recordAssetSample({ bytes, ms: transferMs > 0 ? transferMs : entry.duration });
      }
    });
    observer.observe({ type: 'resource', buffered: true });
  } catch {
    observer = null; // resource timing unavailable — hints and explicit samples still work
  }
}

/** Subscribe to tier changes. Returns an unsubscribe. */
export function onNetTierChange(fn) {
  listeners.add(fn);
  startObserver();
  return () => listeners.delete(fn);
}

/**
 * A ceiling set by the session (features.assetTier), for a venue the host
 * already knows is rough. It can only make the evening lighter, never heavier:
 * a host cannot force 3D onto a phone that measured slow.
 */
export function setSessionTierCap(tier) {
  const next = isTier(tier) ? tier : null;
  if (next === sessionCap) return;
  sessionCap = next;
  emit(netTier());
}

/** For diagnostics and the tier badge in dev. */
export function netTierDebug() {
  return {
    tier: netTier(),
    measured: current ?? measuredTier(),
    kbps: ewmaKbps === null ? null : Math.round(ewmaKbps),
    samples: sampleCount,
    bigSamples,
    hint: hintedTier(),
    sessionCap,
    override: readOverride(),
  };
}

/**
 * Measure the connection against a real file, once, when nothing else has.
 *
 * Needed because the heavy assets are invisible to resource timing (see the
 * header note above) and a returning player's bundle comes off the disk cache,
 * so a table can reach the first wine with no evidence at all. Ranged so it
 * costs a fixed ~200 KB whatever the file is, and cache-busted so it measures
 * the network rather than the disk.
 *
 * @param {string} url  a SAME-ORIGIN asset (no CORS preflight, no masking)
 */
export async function probeConnection(url = '/hero-tasting.mp4', { bytes = 200_000, force = false } = {}) {
  if (probed && !force) return;
  probed = true;
  if (sampleCount > 0 && !force) return; // real traffic already told us
  if (typeof fetch !== 'function') return;
  if (probing) return;
  probing = true;
  lastProbeAt = Date.now();
  try {
    const started = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}netprobe=${started.toFixed(0)}`, {
      headers: { Range: `bytes=0-${bytes - 1}` },
      cache: 'no-store',
    });
    if (!response.ok) return;
    const body = await response.blob();
    const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - started;
    // A server that ignores Range hands back the whole file; body.size is what
    // actually crossed the wire either way.
    // The probe is a deliberate, cache-busting measurement of a fixed size,
    // so it keeps the old floor rather than the bandwidth-only one.
    recordAssetSample({ bytes: body.size, ms: elapsed, minBytes: PROBE_MIN_BYTES, provisional: true });
  } catch {
    // Offline, blocked, or the asset moved. The hint and later samples stand.
  } finally {
    probing = false;
  }
}

// A tier below full stops the game downloading the very things it measures
// (no model, no lighting map), so one slow reading used to stand all evening.
// Measure again at each wine, a little bigger than the first probe so latency
// counts for less, as long as the connection is not plainly slow.
const REPROBE_GAP_MS = 60_000;
const REPROBE_BYTES = 300_000;

/**
 * Re-measure a connection the game has given up on, so a dip costs one wine
 * rather than the night. No-op when the tier is full, forced by the host or
 * the player, measured slow (300 KB there is not free), or measured recently.
 */
export function reprobeIfBelowFull(url) {
  if (readOverride()) return null;
  if (sessionCap && sessionCap !== NET_FULL) return null; // a lighter evening by choice
  const measured = current ?? measuredTier();
  if (measured !== NET_MIDDLE) return null;
  if (Date.now() - lastProbeAt < REPROBE_GAP_MS) return null;
  // Resolves when the reading is in, so a caller can act on the new tier.
  return probeConnection(url, { bytes: REPROBE_BYTES, force: true });
}

/** Test hook — forget everything that was measured. */
export function __resetNetTierForTests() {
  ewmaKbps = null;
  sampleCount = 0;
  current = null;
  pendingUpgrade = null;
  pendingUpgradeCount = 0;
  sessionCap = null;
  listeners.clear();
  try { observer?.disconnect(); } catch { /* already gone */ }
  observer = null;
  observerStarted = false;
  probed = false;
  bigSamples = 0;
  lastProbeAt = 0;
  probing = false;
}
